import type {
  CompetencyDomain,
  FrameworkItem,
  ItemTuple,
  ItemType,
  Role,
  RoleSource,
} from '../types/framework'

/** Number of items a subscriber must choose per type, per domain. */
export const PICKS_PER_TYPE = 2
export const PICKS_PER_DOMAIN = PICKS_PER_TYPE * 2

export const ITEM_TYPE_LABEL: Record<ItemType, string> = {
  characteristic: 'Characteristic',
  behaviour: 'Behaviour',
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

function toItems(tuples: ItemTuple[], type: ItemType): FrameworkItem[] {
  return tuples.map(([no, competency, criterion]) => ({
    no,
    competency,
    criterion,
    type,
  }))
}

/** Converts a transcribed role source into the shape the app consumes. */
export function buildRole(source: RoleSource): Role {
  return {
    id: source.id,
    name: source.name,
    group: source.group,
    domains: source.domains.map(
      (d): CompetencyDomain => ({
        id: `${source.id}-${slugify(d.name)}`,
        name: d.name,
        characteristic: toItems(d.characteristic, 'characteristic'),
        behaviour: toItems(d.behaviour, 'behaviour'),
      }),
    ),
  }
}

export function itemCount(role: Role): number {
  return role.domains.reduce(
    (sum, d) => sum + d.characteristic.length + d.behaviour.length,
    0,
  )
}

export function questionnaireSize(role: Role): number {
  return role.domains.length * PICKS_PER_DOMAIN
}

/** Item IDs are only unique within a role, so selections are keyed as `${roleId}:${no}`. */
export function itemKey(roleId: string, no: number): string {
  return `${roleId}:${no}`
}

export function domainItems(domain: CompetencyDomain): FrameworkItem[] {
  return [...domain.characteristic, ...domain.behaviour]
}

/** How many of a domain's items are selected, split by type. */
export function domainProgress(domain: CompetencyDomain, selected: number[]) {
  const characteristic = domain.characteristic.filter((i) =>
    selected.includes(i.no),
  ).length
  const behaviour = domain.behaviour.filter((i) =>
    selected.includes(i.no),
  ).length
  return {
    characteristic,
    behaviour,
    total: characteristic + behaviour,
    complete:
      characteristic === PICKS_PER_TYPE && behaviour === PICKS_PER_TYPE,
  }
}

/** The selected items for a domain, Characteristic first then Behaviour. */
export function selectedInDomain(
  domain: CompetencyDomain,
  selected: number[],
): FrameworkItem[] {
  return domainItems(domain).filter((i) => selected.includes(i.no))
}

/**
 * A demonstration selection (first two of each type in every domain).
 * Used only to preview filled states, never as a recommendation.
 */
export function sampleSelection(role: Role): number[] {
  return role.domains.flatMap((d) => [
    ...d.characteristic.slice(0, PICKS_PER_TYPE).map((i) => i.no),
    ...d.behaviour.slice(0, PICKS_PER_TYPE).map((i) => i.no),
  ])
}
